import type {
  CountryMaster,
  CountryMasterDTO,
  StateMaster,
  StateMasterDTO,
  CityMasterDTO,
} from '@/api-services/hooks/types';
import type {
  Country,
  CountryFormData,
  State,
  StateFormData,
  City,
  CityFormData,
} from '@/types/geography';
import { getClientIp } from './getClientIp';

const nowIsoString = () => new Date().toISOString();

export const mapStatusFromApi = (status?: string): 'active' | 'inactive' =>
  status?.toLowerCase() === 'active' ? 'active' : 'inactive';

export const mapStatusToApi = (status: 'active' | 'inactive'): string =>
  status === 'active' ? 'ACTIVE' : 'INACTIVE';

export const mapCountryMasterToCountry = (country: CountryMaster): Country => {
  const createdAt = country.createdDate ?? country.modifiedDate ?? nowIsoString();
  const updatedAt = country.modifiedDate ?? country.createdDate ?? createdAt;

  return {
    id: country.countryId !== undefined ? String(country.countryId) : '',
    name: country.countryName ?? '',
    code: country.code ?? '',
    status: mapStatusFromApi(country.status),
    metadata: country.remarks ? { remarks: country.remarks } : undefined,
    createdAt,
    updatedAt,
  };
};

export const mapStateMasterToState = (state: StateMaster): State => {
  const createdAt = state.createdDate ?? state.modifiedDate ?? nowIsoString();
  const updatedAt = state.modifiedDate ?? state.createdDate ?? createdAt;

  return {
    id: state.stateId !== undefined ? String(state.stateId) : '',
    name: state.stateName ?? '',
    code: state.code ?? '',
    countryId:
      state.country?.countryId !== undefined ? String(state.country.countryId) : '',
    country: state.country ? mapCountryMasterToCountry(state.country) : undefined,
    status: mapStatusFromApi(state.status),
    metadata: state.remarks ? { remarks: state.remarks } : undefined,
    createdAt,
    updatedAt,
  };
};

export const mapCityMasterToCity = (city: CityMasterDTO): City => {
  const createdAt = city.createdDate ?? city.modifiedDate ?? nowIsoString();
  const updatedAt = city.modifiedDate ?? city.createdDate ?? createdAt;
  const mappedState = city.state ? mapStateMasterToState(city.state) : undefined;
  const mappedCountry = mappedState?.country;

  return {
    id: city.cityId !== undefined ? String(city.cityId) : '',
    name: city.cityName ?? '',
    stateId: mappedState?.id,
    countryId: mappedCountry?.id ?? '',
    state: mappedState,
    country: mappedCountry,
    status: mapStatusFromApi(city.status),
    metadata: city.remarks ? { remarks: city.remarks } : undefined,
    createdAt,
    updatedAt,
  };
};

export const buildCountryPayload = async (
  formData: CountryFormData,
  id?: string | number,
  existingData?: CountryMaster
): Promise<CountryMaster> => {
  const clientIp = await getClientIp();
  const isEditing = id !== undefined && existingData;
  
  return {
    countryId: id !== undefined ? Number(id) : undefined,
    countryName: formData.name,
    code: formData.code,
    status: mapStatusToApi(formData.status),
    remarks: formData.metadata?.remarks,
    createdSystemIp: isEditing ? (existingData.createdSystemIp || clientIp) : clientIp,
    modifiedSystemIp: clientIp,
    // Preserve existing values when editing
    ...(existingData && {
      createdBy: existingData.createdBy,
      createdDate: existingData.createdDate,
      modifiedBy: existingData.modifiedBy,
      modifiedDate: existingData.modifiedDate,
      isDeletedValue: existingData.isDeletedValue,
      isActive: existingData.isActive,
    }),
  };
};

export const mapCountryToPayload = async (country: Country, existingData?: CountryMaster): Promise<CountryMasterDTO> => {
  const clientIp = await getClientIp();
  return {
    countryId: country.id ? Number(country.id) : undefined,
    countryName: country.name,
    code: country.code,
    status: mapStatusToApi(country.status),
    remarks: country.metadata?.remarks,
    createdSystemIp: existingData?.createdSystemIp || clientIp,
    modifiedSystemIp: clientIp,
    ...(existingData && {
      createdBy: existingData.createdBy,
      createdDate: existingData.createdDate,
      modifiedBy: existingData.modifiedBy,
      modifiedDate: existingData.modifiedDate,
      isDeletedValue: existingData.isDeletedValue,
    }),
  };
};

export const buildStatePayload = async (
  formData: StateFormData,
  id?: string | number,
  existingData?: StateMaster
): Promise<StateMasterDTO> => {
  const clientIp = await getClientIp();
  const isEditing = id !== undefined && existingData;
  
  return {
    stateId: id !== undefined ? Number(id) : undefined,
    stateName: formData.name,
    code: formData.code,
    status: mapStatusToApi(formData.status),
    country: formData.countryId
      ? {
          countryId: Number(formData.countryId),
          // Include system IP fields for nested country if available
          ...(existingData?.country && {
            createdSystemIp: existingData.country.createdSystemIp || clientIp,
            modifiedSystemIp: clientIp,
          }),
        }
      : undefined,
    remarks: formData.metadata?.remarks,
    createdSystemIp: isEditing ? (existingData.createdSystemIp || clientIp) : clientIp,
    modifiedSystemIp: clientIp,
    // Preserve existing values when editing
    ...(existingData && {
      createdBy: existingData.createdBy,
      createdDate: existingData.createdDate,
      modifiedBy: existingData.modifiedBy,
      modifiedDate: existingData.modifiedDate,
      isDeletedValue: existingData.isDeletedValue,
    }),
  };
};

export const mapStateToPayload = async (state: State, existingData?: StateMaster): Promise<StateMasterDTO> => {
  const clientIp = await getClientIp();
  return {
    stateId: state.id ? Number(state.id) : undefined,
    stateName: state.name,
    code: state.code,
    status: mapStatusToApi(state.status),
    country: state.countryId
      ? {
          countryId: Number(state.countryId),
          ...(existingData?.country && {
            createdSystemIp: existingData.country.createdSystemIp || clientIp,
            modifiedSystemIp: clientIp,
          }),
        }
      : undefined,
    remarks: state.metadata?.remarks,
    createdSystemIp: existingData?.createdSystemIp || clientIp,
    modifiedSystemIp: clientIp,
    ...(existingData && {
      createdBy: existingData.createdBy,
      createdDate: existingData.createdDate,
      modifiedBy: existingData.modifiedBy,
      modifiedDate: existingData.modifiedDate,
      isDeletedValue: existingData.isDeletedValue,
    }),
  };
};

export const buildCityPayload = async (
  formData: CityFormData,
  id?: string | number,
  existingData?: CityMasterDTO
): Promise<CityMasterDTO> => {
  const clientIp = await getClientIp();
  const isEditing = id !== undefined && existingData;
  
  return {
    cityId: id !== undefined ? Number(id) : undefined,
    cityName: formData.name,
    status: mapStatusToApi(formData.status),
    state:
      formData.stateId && formData.stateId !== 'no-state'
        ? {
            stateId: Number(formData.stateId),
            // Include system IP fields for nested state if available
            ...(existingData?.state && {
              createdSystemIp: existingData.state.createdSystemIp || clientIp,
              modifiedSystemIp: clientIp,
            }),
            country: formData.countryId
              ? {
                  countryId: Number(formData.countryId),
                  // Include system IP fields for nested country if available
                  ...(existingData?.state?.country && {
                    createdSystemIp: existingData.state.country.createdSystemIp || clientIp,
                    modifiedSystemIp: clientIp,
                  }),
                }
              : undefined,
          }
        : undefined,
    remarks: formData.metadata?.remarks,
    createdSystemIp: isEditing ? (existingData.createdSystemIp || clientIp) : clientIp,
    modifiedSystemIp: clientIp,
    // Preserve existing values when editing
    ...(existingData && {
      createdBy: existingData.createdBy,
      createdDate: existingData.createdDate,
      modifiedBy: existingData.modifiedBy,
      modifiedDate: existingData.modifiedDate,
      isDeletedValue: existingData.isDeletedValue,
    }),
  };
};

export const mapCityToPayload = (city: City): CityMasterDTO => ({
  cityId: city.id ? Number(city.id) : undefined,
  cityName: city.name,
  status: mapStatusToApi(city.status),
  state:
    city.stateId && city.stateId !== 'no-state'
      ? {
          stateId: Number(city.stateId),
          country: city.countryId
            ? {
                countryId: Number(city.countryId),
              }
            : undefined,
        }
      : undefined,
  remarks: city.metadata?.remarks,
});
